import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component, ElementRef,
  inject,
  Input,
  input,
  signal,
  ViewChild,
} from '@angular/core';
import { IconComponent, SquircleComponent } from '@shared/components';
import { AnalyticsEventsEnum } from '@core/models/analytics.model';
import { MediaActionsComponent } from '@shared/components/media-actions/media-actions.component';
import {
  BannerPictureType,
  FeedItem, getSystemVideoObject,
  isFeedItemOfMediaType,
  isFeedItemOfSystemVideoV1Type, isFeedItemOfSystemVideoV2Type, isFeedItemOfUserVideoV2Type,
  MediaActionsMode,
} from '@core/models';
import { BreakpointFacade } from '@core/facades';
import { MediaViewService, OverlayService } from '@core/services';
import { AnalyticsService } from '@core/services/analytics/analytics.service';
import { AboutMediaComponent } from '@shared/components/media-swiper/components/about-media/about-media.component';
import { DonationComponent } from '@shared/components/media-swiper/components/donation/donation.component';
import { SingleDoubleClickDirective } from '@shared/directives';
import { AnalyticsDirective } from '@shared/directives/analytics.directive';
import { mediaActions, mediaSelectors } from '@store/media';
import { Store } from '@ngrx/store';
import { NgTemplateOutlet } from '@angular/common';
import { SystemVideoV1 } from '@api/backend';
import { TopButtonComponent } from '@shared/components/media-swiper/components/top-button/top-button.component';

@Component({
  selector: 'cheelee-media-view',
  standalone: true,
  imports: [
    SquircleComponent,
    MediaActionsComponent,
    AboutMediaComponent,
    DonationComponent,
    SingleDoubleClickDirective,
    AnalyticsDirective,
    NgTemplateOutlet,
    IconComponent,
    TopButtonComponent,
  ],
  templateUrl: './media-view.component.html',
  styleUrl: './media-view.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [MediaViewService],
})
export class MediaViewComponent implements AfterViewInit {

  @ViewChild('videoTpl', { static: true })
  public target!: ElementRef<HTMLElement>;

  @ViewChild('htmlVideoElement')
  public player!: ElementRef<HTMLVideoElement>;

  public feedItem = input.required<FeedItem>();

  public index = input.required<number>();

  public currentIndex = signal<number>(0);

  public isVisible = signal<boolean>(false);

  protected readonly store = inject(Store);

  public readonly isMuted = this.store.selectSignal(mediaSelectors.selectIsMuted);

  protected readonly analyticsEvents = AnalyticsEventsEnum;

  protected readonly isMobile = inject(BreakpointFacade).isMobile;

  protected readonly isDesktop = inject(BreakpointFacade).isDesktop;

  protected readonly overlayService = inject(OverlayService);

  protected readonly analyticsService = inject(AnalyticsService);

  protected readonly mediaViewService = inject(MediaViewService);

  public toggleMute(force = false) {
    this.store.dispatch(mediaActions.muteMedia({ isMuted: force || !this.isMuted() }));
  }

  @Input()
  public mode: MediaActionsMode = MediaActionsMode.RECOMMENDATIONS;

  public readonly canShowPauseEffect = signal(false);

  public readonly canShowLikeEffect = signal(false);

  public readonly canShowVideoSpinner = signal(false);

  protected isMediaType(feedItem: FeedItem) {
    return isFeedItemOfMediaType(feedItem);
  }

  protected isUserVideoType(feedItem: FeedItem) {
    return isFeedItemOfUserVideoV2Type(feedItem);
  }

  protected isAdsType(feedItem: FeedItem) {
    return isFeedItemOfSystemVideoV1Type(feedItem) || isFeedItemOfSystemVideoV2Type(feedItem);
  }

  public getAdObject(item: FeedItem): SystemVideoV1 | null {
    return getSystemVideoObject(item);
  }

  public openGetAppDialog(forceAnalyticsEvent: AnalyticsEventsEnum, picture: BannerPictureType | undefined): void {
    this.overlayService.openGetAppDialog(forceAnalyticsEvent, picture);
    if (forceAnalyticsEvent) {
      this.analyticsService.logEvent(forceAnalyticsEvent, {});
    }
  }

  public ngAfterViewInit() {
    if (!this.player.nativeElement) return;
    // Настраиваем Intersection Observer с порогом видимости 50%
    const observer = new IntersectionObserver((entries, a) => {
      entries.forEach((entry) => {
        this.isVisible.set(entry.isIntersecting);
        if (entry.isIntersecting) {
          this.currentIndex.set(this.index());
          this.mediaViewService.playVideo();
          this.player.nativeElement.play().then();
          if (this.isMobile()) {
            if (this.mode === 'recommendations' && this.index() !== 0 && this.index() % 7 === 0) {
              this.openGetAppDialog(AnalyticsEventsEnum.FeedModalAppear, undefined);
            }
          } else {
            if (this.index() === 0) {
              this.openGetAppDialog(AnalyticsEventsEnum.FeedModalAppear, undefined);
            }
          }

        } else {
          this.mediaViewService.pauseVideo();
          this.player.nativeElement.pause();
        }
      });
    }, {
      threshold: 0.5,  // событие срабатывает, когда 50% элемента видно
    });

    // Начинаем наблюдение за целевым элементом
    observer.observe(this.player.nativeElement);
  }

  private handlePlay(video: HTMLVideoElement): void {
    if (!video.paused && !video.ended) {
      return;
    }

    video.play().then(() => {
      this.mediaViewService.playVideo();
    }).catch(error => {
      if (error.name === 'AbortError') {
        video.play().then(() => {
          this.mediaViewService.playVideo();
        }).catch();
      } else { }
    });
  }

  private handlePause(video: HTMLVideoElement): void {
    this.mediaViewService.pauseVideo();
    video.pause();
  }

  public onVideoClick(): void {
    const video = this.player?.nativeElement as HTMLVideoElement;
    this.canShowPauseEffect.set(true);

    if (video.paused) {
      this.handlePlay(video);
    } else {
      this.handlePause(video);
    }
  }
}
